import { IconTrendingUp } from "@tabler/icons-react";
import { useState } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { BillingSectionHeader } from "@/components/billing/billing-section-header";
import { Card, CardContent } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	applyUsageChartFeatureSelection,
	type BillingUsageItem,
	formatFeatureName,
	sortUsageChartData,
} from "@/lib/billing-usage";
import { cn } from "@/lib/utils";

type UsageTrendsChartProps = {
	data: BillingUsageItem[];
	className?: string;
};

export function UsageTrendsChart({ data, className }: UsageTrendsChartProps) {
	const [selectedFeature, setSelectedFeature] = useState<string | "all">("all");

	const timeSeriesData = data.reduce(
		(acc, item) => {
			const periodKey = new Date(item.period_start).toLocaleDateString(
				"en-US",
				{
					month: "short",
					year: "numeric",
				},
			);

			if (!acc[periodKey]) {
				acc[periodKey] = {
					period: periodKey,
					period_start: item.period_start,
				};
			}

			acc[periodKey][item.feature_key] = item.usage;
			acc[periodKey][`${item.feature_key}_limit`] = item.limit;

			return acc;
		},
		// biome-ignore lint/suspicious/noExplicitAny: Dynamic time series data structure
		{} as Record<string, any>,
	);

	const chartData = sortUsageChartData(Object.values(timeSeriesData));

	const features = [...new Set(data.map((item) => item.feature_key))];
	const filteredFeatures =
		selectedFeature === "all" ? features : [selectedFeature];

	const hasData =
		chartData.length > 0 &&
		chartData.some((d) =>
			filteredFeatures.some((f) => d[f] != null && d[f] > 0),
		);

	const featureFilter = (
		<Select
			value={selectedFeature}
			onValueChange={(v) => {
				setSelectedFeature(applyUsageChartFeatureSelection(v ?? undefined));
			}}
		>
			<SelectTrigger className="h-11 w-full max-sm:text-base sm:h-9 sm:w-[200px]">
				<SelectValue>
					{selectedFeature === "all"
						? "All features"
						: formatFeatureName(selectedFeature)}
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">All features</SelectItem>
				{features.map((feature) => (
					<SelectItem key={feature} value={feature}>
						{formatFeatureName(feature)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);

	return (
		<Card
			className={cn(
				"border border-border bg-card shadow-sm transition-shadow duration-300 hover:shadow-md",
				className,
			)}
		>
			<BillingSectionHeader
				title="Usage Trends"
				description="Historical usage data over time"
				action={featureFilter}
			/>

			<CardContent>
				{hasData ? (
					<ChartContainer
						config={features.reduce((acc, feature, idx) => {
							acc[feature] = {
								label: formatFeatureName(feature),
								color: `hsl(var(--chart-${(idx % 5) + 1}))`,
							};
							return acc;
							// biome-ignore lint/suspicious/noExplicitAny: Dynamic chart data structure
						}, {} as any)}
						className="h-72 w-full sm:h-96 lg:h-[450px]"
					>
						<ResponsiveContainer width="100%" height="100%">
							<LineChart
								data={chartData}
								margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
							>
								<CartesianGrid
									stroke="var(--border)"
									strokeDasharray="3 3"
									opacity={0.2}
								/>
								<XAxis
									dataKey="period"
									tick={{
										fontSize: 12,
										fill: "var(--muted-foreground)",
										fontWeight: 500,
									}}
									axisLine={{ stroke: "var(--border)", strokeWidth: 1 }}
									tickLine={false}
									padding={{ left: 20, right: 20 }}
								/>
								<YAxis
									tick={{
										fontSize: 12,
										fill: "var(--muted-foreground)",
										fontWeight: 500,
									}}
									axisLine={{ stroke: "var(--border)", strokeWidth: 1 }}
									tickLine={false}
									padding={{ top: 20, bottom: 10 }}
									label={{
										value: "Usage",
										angle: -90,
										position: "insideLeft",
										style: {
											fontSize: 12,
											fill: "var(--muted-foreground)",
											fontWeight: 600,
										},
									}}
								/>
								<Tooltip
									contentStyle={{
										background: "var(--popover)",
										border: "1px solid var(--border)",
										color: "var(--foreground)",
										boxShadow: "0 8px 24px 0 rgba(0,0,0,0.15)",
										borderRadius: "12px",
										padding: "16px",
									}}
									labelStyle={{
										color: "var(--foreground)",
										fontWeight: 600,
										marginBottom: "8px",
										fontSize: "14px",
									}}
									cursor={{
										stroke: "var(--border)",
										strokeWidth: 1,
										strokeDasharray: "5 5",
									}}
								/>
								{filteredFeatures.map((feature, idx) => (
									<Line
										key={feature}
										type="monotone"
										dataKey={feature}
										name={formatFeatureName(feature)}
										stroke={`hsl(var(--chart-${(idx % 5) + 1}))`}
										strokeWidth={3}
										dot={{
											r: 5,
											fill: `hsl(var(--chart-${(idx % 5) + 1}))`,
											strokeWidth: 2,
											stroke: "var(--background)",
										}}
										activeDot={{
											r: 8,
											stroke: "var(--background)",
											strokeWidth: 3,
										}}
										connectNulls
										isAnimationActive={true}
										animationDuration={800}
									/>
								))}
							</LineChart>
						</ResponsiveContainer>
					</ChartContainer>
				) : (
					<div className="flex h-72 w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/20 px-4 sm:h-96 lg:h-[450px]">
						<IconTrendingUp className="mb-4 size-16 shrink-0 text-muted-foreground opacity-30 sm:size-12" />
						<p className="mb-2 text-lg/7 font-semibold text-foreground sm:text-base/7">
							No usage trends available
						</p>
						<p className="max-w-[40ch] text-pretty text-center text-base/7 text-muted-foreground sm:text-sm/6">
							{selectedFeature === "all"
								? "Usage data will appear here as features are used over time"
								: `No usage data available for ${formatFeatureName(selectedFeature)} in the selected period`}
						</p>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
